import React from 'react';
import './paperwork.css';
import { COMPANY } from '../../constants/company';

const Header = () => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="20" fill="#1F0068" />
        <path d="M12 22 L18 28 L28 14" fill="none" stroke="#FF5C39" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="leading-tight">
        <div className="font-bold text-[15px] text-[#1F0068]">{COMPANY.shortName}</div>
        <div className="text-[9px] tracking-[0.22em] text-[#FF5C39] uppercase font-semibold">Cancellation Invoice</div>
      </div>
    </div>
    <div className="text-right text-[10px] text-[#1F0068]/70 leading-relaxed">
      <div>{COMPANY.phone}</div>
      <div>{COMPANY.email}</div>
      <div>{COMPANY.registeredOffice.full}</div>
    </div>
  </div>
);

const CancellationInvoice = ({ policy }) => {
  const c = policy.cancellation || {};
  const confirmed = !!c.confirmed;
  const fullName = `${policy.title || ''} ${policy.firstName} ${policy.lastName}`.trim();
  const amount = Number(c.amount || 0);
  const invoiceNo = `DHS/CAN/${policy.planNumber.replace('DHS', '')}`;
  const cancelDate = c.cancelledAt ? new Date(c.cancelledAt) : new Date();
  const paymentDate = c.paymentDate || policy.paymentDue;

  return (
    <div className="a4-doc">
      <div className="a4-brand-strip" />
      <Header />

      <div className="mt-6 grid grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Cancellation Invoice</div>
          <div className="mt-1 flex items-baseline gap-4 text-[11px] text-[#1F0068]/80 flex-wrap">
            <div><span className="font-bold text-[#1F0068]">Invoice No:</span> {invoiceNo}</div>
            <div><span className="font-bold text-[#1F0068]">Issue Date:</span> {cancelDate.toLocaleDateString('en-GB')}</div>
            <div><span className="font-bold text-[#1F0068]">Original Payment Date:</span> {new Date(paymentDate).toLocaleDateString('en-GB')}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Amount Payable</div>
          <div className="text-3xl font-extrabold text-[#FF5C39]">£ {amount.toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-[#FFF6EB] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Billed To</div>
          <div className="mt-2 text-[11px] text-[#1F0068]/85 space-y-1">
            <div><span className="text-[#1F0068]/60">Customer Name:</span> <span className="font-bold text-[#1F0068]">{fullName}</span></div>
            <div><span className="text-[#1F0068]/60">Address:</span> {policy.address1}{policy.address2 ? ', ' + policy.address2 : ''}</div>
            <div><span className="text-[#1F0068]/60">City:</span> {policy.city}</div>
            <div><span className="text-[#1F0068]/60">Postcode:</span> {policy.postcode}</div>
          </div>
        </div>
        <div className="bg-[#FFEDD6] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Plan Summary</div>
          <div className="mt-2 text-[11px] text-[#1F0068]/85 space-y-1">
            <div><span className="text-[#1F0068]/60">Plan Number:</span> <span className="font-bold text-[#1F0068]">{policy.planNumber}</span></div>
            <div><span className="text-[#1F0068]/60">Plan Type:</span> Maintenance Cover Plan (Cancelled)</div>
            <div><span className="text-[#1F0068]/60">Cancellation Date:</span> {cancelDate.toLocaleDateString('en-GB')}</div>
            <div><span className="text-[#1F0068]/60">Payment Method:</span> Direct Bank Transfer</div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Description</div>
        <div className="mt-2 bg-white border border-[#1F0068]/10 rounded-xl p-3">
          <div className="font-bold text-[#1F0068] text-[12px]">Cancellation Settlement — Plan {policy.planNumber}</div>
          <div className="text-[11px] text-[#1F0068]/85 mt-1">
            Settlement in accordance with the Terms &amp; Conditions of your Maintenance Cover Plan. Covers the pro-rata portion of cover used to date and the applicable administration fee.
          </div>
          {c.reason && <div className="text-[11px] text-[#1F0068]/70 mt-1"><b>Reason:</b> {c.reason}</div>}
        </div>
      </div>

      <div className="mt-4 bg-[#1F0068] text-white rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Direct Bank Transfer Details</div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Account Name</span><span className="font-bold">{COMPANY.bank.accountName}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Sort Code</span><span className="font-bold">{COMPANY.bank.sortCode}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Account Number</span><span className="font-bold">{COMPANY.bank.accountNumber}</span></div>
          <div><span className="text-white/70 text-[10px] uppercase tracking-widest font-bold block">Payment Reference</span><span className="font-bold">{policy.planNumber}</span></div>
        </div>
        <p className="mt-3 text-[10px] text-white/70 italic">Please use the correct reference so your payment can be matched promptly.</p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
        <div className="col-span-2 bg-[#FFF6EB] rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Important Information</div>
          <p className="mt-1 text-[10px] text-[#1F0068]/80 leading-relaxed">{confirmed ? 'This cancellation invoice records the settlement paid on the cancellation of your Maintenance Cover Plan. Thank you.' : 'This cancellation invoice is issued in respect of the settlement due on the cancellation of your Maintenance Cover Plan. The amount below is currently OUTSTANDING and remains payable — your plan will only be confirmed as cancelled once this amount has been received and cleared. Please arrange payment within 14 days of the issue date to avoid further recovery action.'}</p>
        </div>
        <div className="bg-white border border-[#1F0068]/10 rounded-xl p-3 text-right text-[11px]">
          <div className="flex justify-between"><span className="text-[#1F0068]/60">Cancellation Amount</span><span className="font-semibold">£ {amount.toFixed(2)}</span></div>
          <div className="flex justify-between mt-2 pt-2 border-t border-[#1F0068]/10"><span className="font-bold text-[#1F0068]">Total Due</span><span className="font-extrabold text-[#FF5C39] text-lg">£ {amount.toFixed(2)}</span></div>
        </div>
      </div>

      <div className="a4-footer">
        <div>Cancellation Invoice {invoiceNo} · Plan {policy.planNumber}</div>
        <div>{COMPANY.name} · Company No. {COMPANY.companyNumber} · {COMPANY.registeredOffice.short}</div>
      </div>
    </div>
  );
};

export default CancellationInvoice;
